import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { Lock, Mail, ArrowRight } from "lucide-react";

const AdminLogin = () => {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || "/admin";
      navigate(from, { replace: true });
    }
  }, [user, location.state, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate(location.state?.from?.pathname || "/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A201C] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Backdrop dots */}
      <div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage: "radial-gradient(circle, #DE9E36 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative w-full max-w-md">
        <Link to="/" className="inline-block mb-10">
          <img
            src="/ekobiotek-logo-white.webp"
            alt="EKOBIOTEK"
            className="h-14 w-auto"
          />
        </Link>

        <div className="bg-[#FDFBF7] p-8 md:p-10">
          <div className="mb-8">
            <span className="overline text-[#DE9E36]">Admin panel</span>
            <h1 className="mt-3 text-3xl font-bold text-[#1A201C] tracking-tight">
              Sign in
            </h1>
            <p className="mt-2 text-sm text-[#4E5750]">
              EKOBIOTEK-UKRAINA management console.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label className="overline text-[#4E5750] block mb-2">Email</label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4E5750]"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="admin-email-input"
                  className="w-full h-12 pl-11 pr-4 border border-[#E5E0D8] bg-white text-[#1A201C] focus:outline-none focus:border-[#DE9E36]"
                />
              </div>
            </div>

            <div>
              <label className="overline text-[#4E5750] block mb-2">Password</label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-[#4E5750]"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="admin-password-input"
                  className="w-full h-12 pl-11 pr-4 border border-[#E5E0D8] bg-white text-[#1A201C] focus:outline-none focus:border-[#DE9E36]"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="admin-login-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-between gap-3 px-5 py-4 bg-[#1A201C] text-[#DE9E36] hover:bg-[#DE9E36] hover:text-[#1A201C] transition-colors text-xs tracking-[0.22em] uppercase font-bold disabled:opacity-60"
            >
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight size={16} />
            </button>
          </form>
        </div>

        <p className="mt-6 text-xs text-white/50 text-center">
          © EKOBIOTEK-UKRAINA, LLC — Protected area
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
